import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { GridColumn as Column, Grid, GridPageChangeEvent } from '@progress/kendo-react-grid'
import { Upload } from 'antd'
import {
  TransactionAdjustmentPreviewUpload,
  TransactionAdjustmentPreviewUploadKey,
} from 'api/Wallet/Types/TransactionAdjustmentPreviewUpload'
import { FlexBox } from 'components/Box'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { infoToast, Toast } from 'components/Toast/Toast'
import { GridFilterState, initialState } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'

interface Props {
  onClose(): void
  onSubmit(uploadFileHistoryId: string): void
  setToast(toast?: Toast): void
}

export const ImportTransactionPage: React.FC<Props> = (props) => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const [paginationState, setPaginationState] = useState<GridFilterState>({ ...initialState() })
  const [fileInfo, setFileInfo] = useState<File>()
  const [data, setData] = useState<TransactionAdjustmentPreviewUpload[]>([])
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)

  const handleSelectFile = (file: File) => {
    if (file.type !== 'text/csv' && file.type !== 'application/vnd.ms-excel') {
      props.setToast(infoToast('Supported format is only csv.'))
      return false
    }
    setFileInfo(file)
    return false
  }

  const handleUploadFile = async () => {
    if (fileInfo) {
      const formData = new FormData()
      formData.append('files', fileInfo)

      const response = await walletsApiClient.uploadTradingAccountAdjustmentPreview(formData)
      setData(response)
    }
  }

  const handleImportFile = () => {
    setConfirmationModalOpen(true)
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    if (data.length > 0) {
      setPaginationState({ ...initialState(), skip: event.page.skip, take: event.page.take })
    }
  }

  const handleConfirmationSubmit = async () => {
    if (fileInfo) {
      try {
        const formData = new FormData()
        formData.append('files', fileInfo)
        const { uploadFileHistoryId } =
          await walletsApiClient.uploadTradingAccountAdjustmentFile(formData)
        props.onSubmit(uploadFileHistoryId)
        setConfirmationModalOpen(false)
      } catch (e) {
        console.error(e)
        setConfirmationModalOpen(false)
      }
    }
  }

  const handleConfirmationCloseModal = () => {
    setConfirmationModalOpen(false)
  }

  return (
    <React.Fragment>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleConfirmationSubmit}
          onCancel={handleConfirmationCloseModal}
          visible={isConfirmationModalOpen}
          contentText='After submitting the form a bulk of transactions with "Completed" status will be created. Do you want to proceed?'
        />
      )}
      <FlexBox margin={24}>
        <Upload beforeUpload={handleSelectFile} showUploadList={false}>
          <Button>{fileInfo?.name ? fileInfo?.name : <>Select File ...</>}</Button>
        </Upload>
        <Button onClick={handleUploadFile}>Upload</Button>
      </FlexBox>
      <Grid
        resizable
        data={data.slice(paginationState.skip, paginationState.take + paginationState.skip)}
        skip={paginationState.skip}
        take={paginationState.take}
        total={data.length}
        onPageChange={handlePageChange}
        pageable={pageableSettings}
        style={{ zIndex: 0 }}
      >
        <Column
          field={TransactionAdjustmentPreviewUploadKey.clientName}
          title='Client'
          minResizableWidth={180}
          width={180}
        />
        <Column
          field={TransactionAdjustmentPreviewUploadKey.companyName}
          title='Company'
          minResizableWidth={180}
          width={180}
        />
        <Column
          field={TransactionAdjustmentPreviewUploadKey.tradingAccount}
          title='Trading Account'
          minResizableWidth={180}
          width={180}
        />
        <Column
          field={TransactionAdjustmentPreviewUploadKey.amount}
          title='Amount'
          minResizableWidth={180}
          width={180}
        />
        <Column
          field={TransactionAdjustmentPreviewUploadKey.transactionType}
          title='Type'
          minResizableWidth={180}
          width={180}
        />
        <Column
          field={TransactionAdjustmentPreviewUploadKey.direction}
          title='Direction'
          minResizableWidth={180}
          width={180}
        />
      </Grid>
      <FlexBox margin='20px auto' alignItems='center' alignContent='center'>
        <Button onClick={handleImportFile} disabled={!fileInfo}>
          Submit
        </Button>
      </FlexBox>
    </React.Fragment>
  )
}
